import {
    faCheck,
    faEye,
    faLink,
    faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ColumnDescription } from "react-bootstrap-table-next";
import { Link } from "react-router-dom";
import { useAtendimentoPage } from "../../../../Context/AtendimentoPage";
import { ListarAtendimentosVinculadosController } from "../../../../Controllers/AssistenciaTecnica/Atendimento/AtendimentoVinculado/AtendimentosVinculadoControllers";
import useAsyncEffect from "../../../../hooks/useAsyncEffect";
import { IAtendimentoVinculado } from "../../../../Interfaces/AssistenciaTecnica/Atendimento/AtendimentoVinculado/AtendimentoVinculadoInterfaces";
import { ACESSOS } from "../../../../Routes/Acessos";
import ModalComponent from "../../../Modais";
import Table from "../../../Table";
import HTMLRemover from "../../../Utils/HTMLRemover";
import OrSpinner from "../../../Utils/OrSpinner";
import CriarAtendimentoVinculado from "./CriarAtendimentoVinculado";

interface IAtendimentoVinculadosProps {
    idCaso: number;
}

export default function AtendimentoVinculados(
    props: IAtendimentoVinculadosProps
) {
    const [show, setShow] = useState(false);

    const { bloquearInteracoes } = useAtendimentoPage();

    const { value: lstAtendimentosVinculados, loading } = useAsyncEffect(
        () => ListarAtendimentosVinculadosController(props.idCaso),
        [props.idCaso]
    );

    const possuiCasoPai = useMemo(
        () =>
            lstAtendimentosVinculados?.some(
                (a) => a.DS_TIPO_CASO === "CASO PAI"
            ),
        [lstAtendimentosVinculados]
    );

    const mostrarBotaoCriarAtendimentoVinculado =
        possuiCasoPai === false && !bloquearInteracoes;

    const AtendimentoVinculadoColumns: Array<
        ColumnDescription<IAtendimentoVinculado>
    > = [
        {
            dataField: "DUMMY_FIELD",
            text: "",
            isDummyField: true,
            formatter: (_, row) =>
                row.ID_CASO === props.idCaso ? (
                    <>
                        <OverlayTrigger
                            overlay={
                                <Tooltip id="ver-atendimento-tooltip">
                                    Atendimento Atual
                                </Tooltip>
                            }
                        >
                            <div className="m-2 text-success">
                                <FontAwesomeIcon icon={faCheck} />
                            </div>
                        </OverlayTrigger>
                    </>
                ) : (
                    <OverlayTrigger
                        overlay={
                            <Tooltip id="ver-atendimento-tooltip">
                                Ver Atendimento
                            </Tooltip>
                        }
                    >
                        <Link
                            to={ACESSOS.ASSISTENCIA.TELAS.ATENDIMENTO.DS_LINK(
                                row.ID_CASO
                            )}
                            className="m-2 btn btn-icon btn-outline-info"
                        >
                            <FontAwesomeIcon icon={faEye} />
                        </Link>
                    </OverlayTrigger>
                ),
        },
        {
            dataField: "DS_TIPO_CASO",
            text: "Ocorrência",
        },
        {
            dataField: "ID_CASO",
            text: "Protocolo",
        },

        {
            dataField: "DS_CASO",
            text: "Descrição",
            formatter: (c) => (
                <HTMLRemover
                    charLimit={40}
                    customLeiaMais="+"
                    customLeiaMenos="-"
                >
                    {c}
                </HTMLRemover>
            ),
        },
        {
            dataField: "DS_HISTORICO",
            text: "Último Histórico",
            formatter: (c) => (
                <HTMLRemover
                    charLimit={40}
                    customLeiaMais="+"
                    customLeiaMenos="-"
                >
                    {c}
                </HTMLRemover>
            ),
        },
        {
            dataField: "DS_ETAPA",
            text: "Etapa",
        },
        {
            dataField: "DS_STATUS",
            text: "Status",
        },
        {
            dataField: "DT_VISTORIA",
            text: "Dt. Vistoria",
            formatter: (c) => (c ? new Date(c).toLocaleDateString() : "-"),
        },
        {
            dataField: "DT_REPARO",
            text: "Dt. Reparo",
            formatter: (c) => (c ? new Date(c).toLocaleDateString() : "-"),
        },
        {
            dataField: "DS_COMODO",
            text: "Cômodo",
        },
        {
            dataField: "DT_CADASTRO",
            text: "Data",
            formatter: (c) => (c ? new Date(c).toLocaleDateString() : "-"),
        },
    ];

    return (
        <div className="card card-custom">
            <OrSpinner
                spinner="capys"
                loading={loading && !lstAtendimentosVinculados}
            >
                {lstAtendimentosVinculados ? (
                    <>
                        {mostrarBotaoCriarAtendimentoVinculado && (
                            <div className="w-100 d-flex justify-content-end p-4">
                                <button
                                    className="btn btn-success d-flex align-items-center g-2"
                                    onClick={() => {
                                        setShow(true);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    Criar Assistência Técnica
                                </button>
                            </div>
                        )}
                        <Table
                            defaultStyle={{
                                align: "center",
                                classes: "p-2",
                            }}
                            tableView={{
                                columns: AtendimentoVinculadoColumns,
                            }}
                            bordered={false}
                            striped
                            columns={AtendimentoVinculadoColumns}
                            data={
                                lstAtendimentosVinculados as IAtendimentoVinculado[]
                            }
                            keyField="ID_VINCULADO"
                            classes="v-middle-cells"
                            noDataIndication={
                                <div className="text-muted text-center">
                                    Esse atendimento não possui nenhum
                                    atendimento vinculado
                                </div>
                            }
                            loading={loading}
                            noSearch
                        />
                    </>
                ) : (
                    <div className="text-muted text-center">
                        Não foi possível carregar as assistencias técnicas
                    </div>
                )}
            </OrSpinner>

            <ModalComponent
                setShow={setShow}
                show={show}
                ModalComponent={{
                    headerComponent: "Criar Assistência Técnica",
                    IconHeader: faLink,
                }}
            >
                <CriarAtendimentoVinculado
                    idCaso={props.idCaso}
                    setShow={setShow}
                />
            </ModalComponent>
        </div>
    );
}
